import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, TrendingDown, Users, DollarSign, Heart, MessageCircle, Calendar, Activity, XCircle, BarChart3, Sparkles, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TransformationFunnel from './admin/TransformationFunnel';
import ProductAnalytics from './admin/ProductAnalytics';

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1000, prefix = '', suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const target = parseFloat(value) || 0;
    const increment = target / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return (
    <span>
      {prefix}{count.toFixed(decimals)}{suffix}
    </span>
  );
};

// Glassmorphism Stat Card Component
const GlassStatCard = ({ 
  icon: Icon, 
  label, 
  value, 
  trend, 
  trendValue, 
  sparklineData,
  gradient = 'from-rose-400 to-pink-500',
  prefix = '',
  suffix = '',
  decimals = 0
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getTrendIcon = () => {
    if (!trend || trend === 'neutral') return <Minus className="w-3 h-3" />;
    return trend === 'up' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const getTrendColor = () => {
    if (!trend || trend === 'neutral') return 'text-gray-500';
    return trend === 'up' ? 'text-green-500' : 'text-red-500';
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-6 transition-all duration-500 cursor-pointer backdrop-blur-xl ${
        isHovered ? 'shadow-2xl scale-105' : 'shadow-lg'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
        border: '1px solid rgba(255, 182, 193, 0.3)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated gradient background on hover */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 transition-opacity duration-500 ${
          isHovered ? 'opacity-10' : ''
        }`}
      />

      {/* Icon with gradient background */}
      <div className="relative flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        
        {/* Trend indicator */}
        {trend && (
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getTrendColor()} bg-white/50`}>
            {getTrendIcon()}
            <span className="text-xs font-semibold">{trendValue}</span>
          </div>
        )}
      </div>

      {/* Label */}
      <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>

      {/* Value with animated counter */}
      <div className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3">
        <AnimatedCounter value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </div>

      {/* Sparkline */}
      {sparklineData && sparklineData.length > 0 && (
        <div className="h-12 -mx-2 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData}>
              <defs>
                <linearGradient id={`gradient-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFB6C1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#FFB6C1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="#FFB6C1"
                strokeWidth={2}
                fill={`url(#gradient-${label})`}
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Shimmer effect on hover */}
      {isHovered && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
            animation: 'shimmer 2s infinite',
          }}
        />
      )}
    </div>
  );
};

// Loading Skeleton Component
const SkeletonCard = () => (
  <div className="relative overflow-hidden rounded-2xl p-6 backdrop-blur-xl shadow-lg animate-pulse"
       style={{
         background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
         border: '1px solid rgba(255, 182, 193, 0.3)',
       }}>
    <div className="flex items-start justify-between mb-4">
      <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl"></div>
      <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
    </div>
    <div className="w-24 h-4 bg-gray-200 rounded mb-2"></div>
    <div className="w-32 h-8 bg-gray-300 rounded mb-3"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#f43f5e', '#ec4899', '#a855f7', '#8b5cf6', '#6366f1'];

// Add shimmer animation CSS
const shimmerStyle = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

// Inject CSS into document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = shimmerStyle;
  document.head.appendChild(styleSheet);
}

const DashboardView = ({ adminToken, adminUser }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard/stats?time_range=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, subValue, icon: Icon, color, trend, className }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow ${className || ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 font-sans mb-1">{title}</p>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{value}</h3>
          {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
          {trend !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
              {trend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : trend < 0 ? <TrendingDown className="w-4 h-4 mr-1" /> : null}
              <span>{trend > 0 ? '+' : ''}{Math.abs(trend)}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6 p-2 md:p-0">
        {/* Loading Header */}
        <div className="relative overflow-hidden rounded-3xl p-8 mb-8 animate-pulse"
             style={{
               background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
               backdropFilter: 'blur(20px)',
               border: '1px solid rgba(255, 182, 193, 0.2)',
             }}>
          <div className="flex justify-between items-center">
            <div className="space-y-3">
              <div className="w-80 h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg"></div>
              <div className="w-96 h-6 bg-gray-200 rounded"></div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-20 h-10 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Skeleton Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>

        {/* Loading Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-lg animate-pulse">
              <div className="w-48 h-6 bg-gray-200 rounded mb-4"></div>
              <div className="w-full h-64 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>

        {/* Loading Engagement */}
        <div className="space-y-4">
          <div className="w-64 h-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
        <Button onClick={fetchDashboardData} className="mt-4">Retry</Button>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="p-8 text-center text-gray-600">No data available</div>;
  }

  // Prepare data for charts
  const userActivityData = [
    { name: 'DAU', value: dashboardData.users.dau, color: '#f43f5e' },
    { name: 'WAU', value: dashboardData.users.wau, color: '#ec4899' },
    { name: 'MAU', value: dashboardData.users.mau, color: '#a855f7' },
  ];

  const planDistributionData = [
    { name: 'Free', value: dashboardData.plans.free || 0 },
    { name: 'Trial', value: dashboardData.plans.trial || 0 },
    { name: 'Paid', value: dashboardData.plans.paid || 0 },
  ];
  
  const hasPlanData = planDistributionData.some(item => item.value > 0);

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Enhanced Header with Glassmorphism */}
      <div className="relative overflow-hidden rounded-3xl p-8 mb-8"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
             backdropFilter: 'blur(20px)',
             border: '1px solid rgba(255, 182, 193, 0.2)',
           }}>
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/30 via-pink-50/20 to-purple-100/30" />
        
        <div className="relative flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="space-y-2">
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 text-lg">
              Welcome back! Here's what's happening with your community 💕
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Time Range Filter */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-600 self-center mr-2">Time Range:</span>
              {['today', '7d', '30d', 'all'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    timeRange === range
                      ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-200'
                      : 'bg-white/70 text-gray-600 hover:bg-white/90 border border-rose-200'
                  }`}
                >
                  {range === 'today' ? 'Today' : range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : 'All Time'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top Metrics Row - Glassmorphism Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <GlassStatCard
          icon={Users}
          label="New Users"
          value={dashboardData.users.total}
          trend="up"
          trendValue="12%"
          gradient="from-blue-400 to-blue-600"
          sparklineData={[
            { value: dashboardData.users.total * 0.7 },
            { value: dashboardData.users.total * 0.8 },
            { value: dashboardData.users.total * 0.85 },
            { value: dashboardData.users.total * 0.9 },
            { value: dashboardData.users.total }
          ]}
        />
        <GlassStatCard
          icon={Activity}
          label="Active Users"
          value={dashboardData.users.mau}
          trend="up"
          trendValue="8%"
          gradient="from-purple-400 to-purple-600"
          sparklineData={[
            { value: dashboardData.users.mau * 0.75 },
            { value: dashboardData.users.mau * 0.82 },
            { value: dashboardData.users.mau * 0.88 },
            { value: dashboardData.users.mau * 0.95 },
            { value: dashboardData.users.mau }
          ]}
        />
        <GlassStatCard
          icon={Heart}
          label="Paid Members"
          value={dashboardData.plans.paid}
          trend="up"
          trendValue="15%"
          gradient="from-green-400 to-green-600"
          sparklineData={[
            { value: dashboardData.plans.paid * 0.6 },
            { value: dashboardData.plans.paid * 0.7 },
            { value: dashboardData.plans.paid * 0.8 },
            { value: dashboardData.plans.paid * 0.9 },
            { value: dashboardData.plans.paid }
          ]}
        />
        <GlassStatCard
          icon={DollarSign}
          label="Revenue"
          value={dashboardData.revenue.mrr}
          trend="up"
          trendValue="22%"
          prefix="$"
          gradient="from-rose-400 to-pink-500"
          sparklineData={[
            { value: dashboardData.revenue.mrr * 0.65 },
            { value: dashboardData.revenue.mrr * 0.75 },
            { value: dashboardData.revenue.mrr * 0.85 },
            { value: dashboardData.revenue.mrr * 0.92 },
            { value: dashboardData.revenue.mrr }
          ]}
        />
      </div>

      {/* Charts Row 1: User Activity & Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Activity Chart */}
        <div className="relative overflow-hidden rounded-2xl p-6 backdrop-blur-xl shadow-lg"
             style={{
               background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
               border: '1px solid rgba(255, 182, 193, 0.3)',
             }}>
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-rose-500" />
            User Activity
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={userActivityData}>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#ec4899" stopOpacity={0.7}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#FFB6C1" opacity={0.3} />
              <XAxis dataKey="name" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(255, 182, 193, 0.3)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Bar dataKey="value" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Plan Distribution Chart */}
        <div className="relative overflow-hidden rounded-2xl p-6 backdrop-blur-xl shadow-lg"
             style={{
               background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
               border: '1px solid rgba(255, 182, 193, 0.3)',
             }}>
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            Plan Distribution
          </h3>
          {hasPlanData ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={planDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={90}
                  innerRadius={50}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={5}
                >
                  {planDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(255, 182, 193, 0.3)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Users className="w-16 h-16 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No user plan data available</p>
                <p className="text-xs mt-1">Users will appear here once they sign up</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Engagement Metrics - Glassmorphism Cards */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-rose-500" />
          Engagement Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <GlassStatCard
            icon={Heart}
            label="Avg Love Score"
            value={dashboardData.engagement.avg_love_score}
            trend="up"
            trendValue="5%"
            decimals={1}
            gradient="from-rose-400 to-pink-500"
            sparklineData={[
              { value: dashboardData.engagement.avg_love_score * 0.8 },
              { value: dashboardData.engagement.avg_love_score * 0.85 },
              { value: dashboardData.engagement.avg_love_score * 0.9 },
              { value: dashboardData.engagement.avg_love_score * 0.95 },
              { value: dashboardData.engagement.avg_love_score }
            ]}
          />
          <GlassStatCard
            icon={MessageCircle}
            label="Journal Entries"
            value={dashboardData.engagement.journal_entries_today}
            trend="up"
            trendValue="18%"
            gradient="from-purple-400 to-violet-500"
            sparklineData={[
              { value: dashboardData.engagement.journal_entries_today * 0.6 },
              { value: dashboardData.engagement.journal_entries_today * 0.7 },
              { value: dashboardData.engagement.journal_entries_today * 0.8 },
              { value: dashboardData.engagement.journal_entries_today * 0.9 },
              { value: dashboardData.engagement.journal_entries_today }
            ]}
          />
          <GlassStatCard
            icon={Sparkles}
            label="Affirmations"
            value={dashboardData.engagement.affirmations_today}
            trend="up"
            trendValue="25%"
            gradient="from-blue-400 to-indigo-500"
            sparklineData={[
              { value: dashboardData.engagement.affirmations_today * 0.5 },
              { value: dashboardData.engagement.affirmations_today * 0.65 },
              { value: dashboardData.engagement.affirmations_today * 0.8 },
              { value: dashboardData.engagement.affirmations_today * 0.9 },
              { value: dashboardData.engagement.affirmations_today }
            ]}
          />
          <GlassStatCard
            icon={Calendar}
            label="Avg Streak"
            value={dashboardData.engagement.avg_streak}
            trend="up"
            trendValue="3%"
            suffix=" days"
            decimals={1}
            gradient="from-green-400 to-emerald-500"
            sparklineData={[
              { value: dashboardData.engagement.avg_streak * 0.85 },
              { value: dashboardData.engagement.avg_streak * 0.88 },
              { value: dashboardData.engagement.avg_streak * 0.92 },
              { value: dashboardData.engagement.avg_streak * 0.96 },
              { value: dashboardData.engagement.avg_streak }
            ]}
          />
          <GlassStatCard
            icon={MessageCircle}
            label="AI Companion Chats"
            value={dashboardData.engagement.ai_companion_interactions || 0}
            trend="up"
            trendValue="12%"
            gradient="from-cyan-400 to-blue-500"
            sparklineData={[
              { value: (dashboardData.engagement.ai_companion_interactions || 0) * 0.6 },
              { value: (dashboardData.engagement.ai_companion_interactions || 0) * 0.75 },
              { value: (dashboardData.engagement.ai_companion_interactions || 0) * 0.85 },
              { value: (dashboardData.engagement.ai_companion_interactions || 0) * 0.95 },
              { value: dashboardData.engagement.ai_companion_interactions || 0 }
            ]}
          />
          <GlassStatCard
            icon={Users}
            label="Avg Chats Per User"
            value={dashboardData.engagement.avg_companion_messages_per_user || 0}
            trend="up"
            trendValue="8%"
            decimals={1}
            gradient="from-teal-400 to-cyan-500"
            sparklineData={[
              { value: (dashboardData.engagement.avg_companion_messages_per_user || 0) * 0.7 },
              { value: (dashboardData.engagement.avg_companion_messages_per_user || 0) * 0.8 },
              { value: (dashboardData.engagement.avg_companion_messages_per_user || 0) * 0.9 },
              { value: (dashboardData.engagement.avg_companion_messages_per_user || 0) * 0.95 },
              { value: dashboardData.engagement.avg_companion_messages_per_user || 0 }
            ]}
          />
        </div>
      </div>

      {/* Revenue Details - Glassmorphism */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-green-500" />
          Revenue Insights
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <GlassStatCard
            icon={DollarSign}
            label="Annual Run Rate"
            value={dashboardData.revenue.arr}
            trend="up"
            trendValue="35%"
            prefix="$"
            gradient="from-green-400 to-emerald-600"
            sparklineData={[
              { value: dashboardData.revenue.arr * 0.6 },
              { value: dashboardData.revenue.arr * 0.72 },
              { value: dashboardData.revenue.arr * 0.84 },
              { value: dashboardData.revenue.arr * 0.92 },
              { value: dashboardData.revenue.arr }
            ]}
          />
          
          <GlassStatCard
            icon={TrendingUp}
            label="Conversion Rate"
            value={dashboardData.plans.conversion_rate}
            trend="up"
            trendValue="4%"
            suffix="%"
            decimals={1}
            gradient="from-blue-400 to-indigo-600"
            sparklineData={[
              { value: dashboardData.plans.conversion_rate * 0.85 },
              { value: dashboardData.plans.conversion_rate * 0.88 },
              { value: dashboardData.plans.conversion_rate * 0.92 },
              { value: dashboardData.plans.conversion_rate * 0.96 },
              { value: dashboardData.plans.conversion_rate }
            ]}
          />
          
          <GlassStatCard
            icon={XCircle}
            label="Cancellations"
            value={dashboardData.revenue.cancellations_this_month}
            trend="down"
            trendValue="2%"
            suffix=" this month"
            gradient="from-orange-400 to-red-500"
            sparklineData={[
              { value: dashboardData.revenue.cancellations_this_month * 1.3 },
              { value: dashboardData.revenue.cancellations_this_month * 1.15 },
              { value: dashboardData.revenue.cancellations_this_month * 1.08 },
              { value: dashboardData.revenue.cancellations_this_month * 1.02 },
              { value: dashboardData.revenue.cancellations_this_month }
            ]}
          />
        </div>
      </div>

      {/* Community Stats - Glassmorphism */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-rose-500" />
          Community Activity
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <GlassStatCard
            icon={MessageCircle}
            label="Community Posts"
            value={dashboardData.community.total_posts}
            trend="up"
            trendValue="24%"
            gradient="from-rose-400 to-pink-600"
            sparklineData={[
              { value: dashboardData.community.total_posts * 0.65 },
              { value: dashboardData.community.total_posts * 0.75 },
              { value: dashboardData.community.total_posts * 0.85 },
              { value: dashboardData.community.total_posts * 0.93 },
              { value: dashboardData.community.total_posts }
            ]}
          />
          
          <GlassStatCard
            icon={Users}
            label="Active Channels"
            value={dashboardData.community.total_channels}
            trend="neutral"
            trendValue="0%"
            gradient="from-purple-400 to-violet-600"
            sparklineData={[
              { value: dashboardData.community.total_channels },
              { value: dashboardData.community.total_channels },
              { value: dashboardData.community.total_channels },
              { value: dashboardData.community.total_channels },
              { value: dashboardData.community.total_channels }
            ]}
          />
        </div>
      </div>

      {/* Transformation Funnel */}
      <TransformationFunnel timeRange={timeRange} />

      {/* Product Analytics */}
      <ProductAnalytics timeRange={timeRange} />

      {/* Last Updated - Elegant Footer */}
      <div className="relative overflow-hidden rounded-2xl p-4 backdrop-blur-xl mt-8"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.2)',
           }}>
        <p className="text-center text-sm text-gray-600 flex items-center justify-center gap-2">
          <Activity className="w-4 h-4 text-rose-400" />
          Last updated: {new Date(dashboardData.generated_at + 'Z').toLocaleString('en-US', { 
            timeZone: 'America/New_York',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })} {new Date().toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }).split(' ').pop()}
        </p>
      </div>
    </div>
  );
};

export default DashboardView;